import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Link2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { ImageUpload } from "./ImageUpload";

const inputCls = "w-full bg-[#070709] border border-[#232530] px-4 py-2.5 text-[#ede8e1] font-light placeholder:text-[#57534e] focus:border-[#581313] focus:outline-none transition-colors text-sm";
const EMPTY = { section: "", title: "", slug: "", excerpt: "", body: "", cover_image: "", tags: "", status: "draft", featured: false, external_url: "", series: "", series_order: 0, motif: "", cat: "" };

export const PostsTab = () => {
  const [posts, setPosts] = useState([]);
  const [sections, setSections] = useState([]);
  const [editing, setEditing] = useState(null); // {id?} + form fields
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/admin/posts"), api.get("/admin/sections")]);
    setPosts(p.data);
    setSections(s.data);
  };

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, []);

  const startNew = () => setEditing({ ...EMPTY, section: sections[0]?.slug || "" });
  const startEdit = (p) => setEditing({ ...p, tags: (p.tags || []).join(", ") });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      section: editing.section, title: editing.title, slug: editing.slug || undefined,
      excerpt: editing.excerpt, body: editing.body, cover_image: editing.cover_image,
      tags: editing.tags.split(",").map((t) => t.trim()).filter(Boolean),
      status: editing.status, featured: !!editing.featured, external_url: editing.external_url,
      series: editing.series || "", series_order: Number(editing.series_order) || 0,
      motif: editing.motif || "", cat: editing.cat || "",
    };
    try {
      if (editing.id) await api.put(`/admin/posts/${editing.id}`, payload);
      else await api.post("/admin/posts", payload);
      toast.success(editing.status === "published" ? "Entry published to the Vault." : "Entry saved as draft.");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Cast "${p.title}" into the fire? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/posts/${p.id}`);
      toast.success("Entry destroyed.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (editing) {
    return (
      <form onSubmit={save} className="border border-[#232530] bg-[#0d0e12] p-6 sm:p-8 space-y-5" data-testid="cms-post-editor">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl text-[#ede8e1]">{editing.id ? "Edit Entry" : "New Entry"}</h2>
          <button type="button" onClick={() => setEditing(null)} data-testid="cms-cancel-edit-button"
            className="text-[#78716c] hover:text-[#581313] transition-colors"><X size={20} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <input required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
            placeholder="Title" className={inputCls} data-testid="cms-post-title-input" />
          <select value={editing.section} onChange={(e) => setEditing({ ...editing, section: e.target.value })}
            className={inputCls} data-testid="cms-post-section-select">
            {sections.map((s) => <option key={s.id} value={s.slug}>{s.title}</option>)}
          </select>
        </div>
        <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
          placeholder="Slug (optional — carved from the title)" className={inputCls} data-testid="cms-post-slug-input" />
        <textarea rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
          placeholder="Excerpt — one haunting line" className={`${inputCls} resize-none`} data-testid="cms-post-excerpt-input" />
        <ImageUpload value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} testid="cms-post-cover" />
        <textarea rows={12} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
          placeholder="Body — Markdown supported (# headings, > quotes, **bold**)" className={`${inputCls} font-mono`} data-testid="cms-post-body-input" />
        <div className="grid sm:grid-cols-3 gap-5">
          <input value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })}
            placeholder="Tags, comma separated" className={inputCls} data-testid="cms-post-tags-input" />
          <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}
            className={inputCls} data-testid="cms-post-status-select">
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
          <input value={editing.external_url} onChange={(e) => setEditing({ ...editing, external_url: e.target.value })}
            placeholder="External link (optional)" className={inputCls} data-testid="cms-post-external-input" />
        </div>
        <div className="grid sm:grid-cols-3 gap-5">
          <input value={editing.series || ""} onChange={(e) => setEditing({ ...editing, series: e.target.value })}
            placeholder="Series name (optional)" className={inputCls} data-testid="cms-post-series-input" />
          <input type="number" min="0" value={editing.series_order ?? 0}
            onChange={(e) => setEditing({ ...editing, series_order: e.target.value })}
            placeholder="Chapter №" className={inputCls} data-testid="cms-post-series-order-input" />
          <select value={editing.motif || ""} onChange={(e) => setEditing({ ...editing, motif: e.target.value })}
            className={inputCls} data-testid="cms-post-motif-select">
            <option value="">Frame motif — auto</option>
            <option value="0">Frame motif — pointed arch</option>
            <option value="1">Frame motif — thorn branch</option>
            <option value="2">Frame motif — vesica piscis</option>
          </select>
          <select value={editing.cat || ""} onChange={(e) => setEditing({ ...editing, cat: e.target.value })}
            className={inputCls} data-testid="cms-post-cat-select">
            <option value="">Black cat — auto pose</option>
            <option value="0">Cat — sitting</option>
            <option value="1">Cat — standing alert</option>
            <option value="2">Cat — pouncing</option>
            <option value="3">Cat — rolling play</option>
            <option value="4">Cat — dancing</option>
            <option value="none">No cat on this entry</option>
          </select>
        </div>
        <label className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-[#a8a29e] cursor-pointer">
          <input type="checkbox" checked={!!editing.featured}
            onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}
            className="accent-[#581313] w-4 h-4" data-testid="cms-post-featured-checkbox" />
          Featured entry
        </label>
        <button type="submit" disabled={busy} data-testid="cms-save-post-button"
          className="bg-[#581313] hover:bg-[#581313] disabled:opacity-50 text-[#ede8e1] border border-[#581313]/40 font-mono text-xs uppercase tracking-widest px-8 py-3 transition-colors">
          {busy ? "Sealing…" : "Save Entry"}
        </button>
      </form>
    );
  }

  return (
    <div data-testid="cms-posts-list">
      <div className="flex items-center justify-between mb-6">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#78716c]">{posts.length} entries in the Vault</p>
        <button onClick={startNew} data-testid="cms-create-post-button"
          className="flex items-center gap-2 bg-[#581313] hover:bg-[#581313] text-[#ede8e1] border border-[#581313]/40 font-mono text-xs uppercase tracking-widest px-5 py-2.5 transition-colors">
          <Plus size={14} /> New Entry
        </button>
      </div>
      <div className="border border-[#232530] divide-y divide-[#232530]">
        {posts.map((p) => (
          <div key={p.id} className="flex items-center gap-4 px-5 py-4 bg-[#0d0e12] hover:bg-[#14151b] transition-colors" data-testid={`cms-post-row-${p.slug}`}>
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg text-[#ede8e1] truncate">{p.title}</p>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716c] mt-1">
                {p.section} — {new Date(p.created_at).toLocaleDateString("en-GB")} — {p.views || 0} reads
                {p.series && <span> — {p.series} №{p.series_order || 0}</span>}
                {p.featured && <span className="text-[#581313]"> — featured</span>}
              </p>
            </div>
            <span className={`font-mono text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${
              p.status === "published" ? "text-[#581313] border-[#581313]/50" : "text-[#78716c] border-[#232530]"}`}>
              {p.status}
            </span>
            <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/preview/${p.preview_token}`); toast.success("Preview link copied — opens even while unpublished."); }}
              data-testid={`cms-preview-post-${p.slug}`} title="Copy draft preview link"
              className="text-[#a8a29e] hover:text-[#ede8e1] transition-colors"><Link2 size={16} /></button>
            <button onClick={() => startEdit(p)} data-testid={`cms-edit-post-${p.slug}`}
              className="text-[#a8a29e] hover:text-[#ede8e1] transition-colors"><Pencil size={16} /></button>
            <button onClick={() => remove(p)} data-testid={`cms-delete-post-${p.slug}`}
              className="text-[#a8a29e] hover:text-[#581313] transition-colors"><Trash2 size={16} /></button>
          </div>
        ))}
        {posts.length === 0 && (
          <p className="px-5 py-12 text-center font-mono text-xs tracking-[0.3em] uppercase text-[#57534e]">The Vault is empty. Write something.</p>
        )}
      </div>
    </div>
  );
};
